"use client";

import React, { createContext, useCallback, useContext, useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useFilesStore, WorkspaceFile } from "@/stores/files-store";
import { usePanesStore, PaneState, PaneViewType, ViewModeType } from "@/stores/panes-store";
import { useTasksStore, Task } from "@/stores/tasks-store";
import { useHistoryStore, HistoryEntry } from "@/stores/history-store";
import { useGraphData, GraphData } from "@/stores/graph-store";
import { apiRequest } from "@/lib/api";
import { toast } from "@/components/ui/toast";

type PaneViewTypeCompat = "editor" | "graph" | "settings" | "welcome";
type ViewModeTypeCompat = "web" | "normal";

interface WorkspaceContextProps {
  files: WorkspaceFile[];
  activePane: "left" | "right";
  isSplit: boolean;
  leftPane: PaneState;
  rightPane: PaneState;
  currentViewMode: ViewModeType;
  selectedFileIds: string[];
  toggleSelectFile: (fileId: string, multi?: boolean) => void;
  clearSelection: () => void;
  selectedProject: string;
  selectedMember: string;
  selectedTimeframe: "all" | "week" | "month";
  tasks: Task[];
  workspaceHistory: HistoryEntry[];
  projects: string[];
  members: string[];
  setCurrentViewMode: (mode: ViewModeType) => void;
  reorderTabs: (fromIndex: number, toIndex: number, pane: "left" | "right") => void;
  setSelectedProject: (proj: string) => void;
  setSelectedMember: (member: string) => void;
  setSelectedTimeframe: (tf: "all" | "week" | "month") => void;
  setActivePane: (pane: "left" | "right") => void;
  setIsSplit: (split: boolean) => void;
  openFile: (fileId: string, pane?: "left" | "right" | "current" | "split") => void;
  closeFile: (fileId: string, pane: "left" | "right") => void;
  setActiveTab: (fileId: string, pane: "left" | "right") => void;
  setEditorMode: (mode: "edit" | "preview" | "flow", pane: "left" | "right") => void;
  setPaneViewType: (viewType: PaneViewType, pane: "left" | "right") => void;
  updateFileContent: (fileId: string, content: string) => void;
  updateFileTitle: (fileId: string, title: string) => void;
  createFile: (name: string, parentId?: string, isFolder?: boolean, project?: string) => string;
  deleteFile: (fileId: string) => void;
  moveFile: (fileId: string, newParentId: string | undefined) => boolean;
  addTask: (nodeId: string, title: string, assignee: string, timeframe?: "week" | "month") => void;
  toggleTask: (taskId: string) => void;
  deleteTask: (taskId: string) => void;
  graphData: GraphData;
  getBacklinks: (fileId: string) => WorkspaceFile[];
  addHistoryEntry: (nodeId: string, nodeName: string, action: string, user: string) => void;
}

const WorkspaceContext = createContext<WorkspaceContextProps | undefined>(undefined);

export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const filesStore = useFilesStore();
  const panesStore = usePanesStore();
  const tasksStore = useTasksStore();
  const historyStore = useHistoryStore();

  const [selectedProject, setSelectedProject] = React.useState<string>("all");
  const [selectedFileIds, setSelectedFileIds] = React.useState<string[]>([]);

  const toggleSelectFile = React.useCallback((fileId: string, multi: boolean = false) => {
    setSelectedFileIds((prev) => {
      if (multi) {
        return prev.includes(fileId) ? prev.filter((id) => id !== fileId) : [...prev, fileId];
      }
      return prev.includes(fileId) ? [] : [fileId];
    });
  }, []);

  const clearSelection = React.useCallback(() => setSelectedFileIds([]), []);
  const [selectedMember, setSelectedMember] = React.useState<string>("all");
  const [selectedTimeframe, setSelectedTimeframe] = React.useState<"all" | "week" | "month">("all");
  const [members, setMembers] = React.useState<string[]>([]);
  const nameToIdRef = React.useRef<Map<string, string>>(new Map());

  useEffect(() => {
    filesStore.loadWorkspace();
    historyStore.loadFromAPI();
    tasksStore.fetchTasks();
    tasksStore.fetchSummary();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const fileIdParam = params.get("fileId");
    if (fileIdParam && filesStore.files.length > 0 && !filesStore.loading) {
      const fileExists = filesStore.files.some((f) => f.id === fileIdParam);
      if (fileExists) {
        panesStore.setCurrentViewMode("normal");
        openFile(fileIdParam);
        const newUrl = window.location.pathname;
        window.history.replaceState({}, "", newUrl);
      }
    }
  }, [filesStore.files, filesStore.loading]);

  useEffect(() => {
    (async () => {
      try {
        const users = await apiRequest<{ items: { id: string; name: string; email: string }[] }>("/v1/users");
        const userList = users?.items || users || [];
        const names = userList.map((u: any) => u.name || u.email);
        setMembers(names);
        const map = new Map<string, string>();
        userList.forEach((u: any) => map.set(u.name || u.email, u.id));
        nameToIdRef.current = map;
      } catch {
        setMembers(["Admin"]);
      }
    })();
  }, []);

  const graphData = useGraphData(selectedProject, selectedMember, selectedTimeframe);

  const openFile = async (fileId: string, targetPane: "left" | "right" | "current" | "split" = "current") => {
    let pane: "left" | "right" = panesStore.activePane;
    if (targetPane === "left") pane = "left";
    else if (targetPane === "right") {
      pane = "right";
      if (!panesStore.isSplit) panesStore.setIsSplit(true);
    } else if (targetPane === "split") {
      pane = panesStore.activePane === "left" ? "right" : "left";
      if (!panesStore.isSplit) panesStore.setIsSplit(true);
    }
    panesStore.openFile(fileId, pane);
    if (pathname && pathname !== "/workspace") {
      router.push("/workspace");
    }

    const targetFile = filesStore.files.find((f) => f.id === fileId);
    if (targetFile && targetFile.type === "file" && !targetFile.content && targetFile.documentId) {
      try {
        const versions = await apiRequest<any[]>(`/v1/documents/${targetFile.documentId}/versions`);
        if (versions?.length > 0) {
          const content = versions[0].plain_text || "";
          filesStore.hydrateFileContent(fileId, content);
        }
      } catch {}
    }
  };

  const createFile = (name: string, parentId?: string, isFolder: boolean = false, project?: string): string => {
    const projectName = project || (selectedProject === "all" ? "Wiki Arsenal" : selectedProject);
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    filesStore.createFile(name, parentId, isFolder, projectName, tempId).then((realId) => {
      panesStore.swapFileId(tempId, realId);
    }).catch(() => {});
    return tempId;
  };

  const getBacklinks = (fileId: string): WorkspaceFile[] => {
    const currentFile = filesStore.files.find((f) => f.id === fileId);
    if (!currentFile || currentFile.type !== "file") return [];
    const currentName = currentFile.name.replace(".md", "").toLowerCase().trim();
    return filesStore.files.filter((file) => {
      if (!file.content || file.id === fileId || file.type !== "file") return false;
      const regex = /\[\[([^\]]+)\]\]/g;
      let match;
      while ((match = regex.exec(file.content)) !== null) {
        const linkedName = match[1].toLowerCase().trim().replace(".md", "");
        if (linkedName === currentName) return true;
      }
      return false;
    });
  };

  // Task actions — bridge the synchronous context API to the async backend store
  const addTask = useCallback((nodeId: string, title: string, assignee: string, timeframe?: "week" | "month") => {
    const targetFile = filesStore.files.find((f) => f.id === nodeId);
    const assigneeId = assignee && assignee !== "all" && assignee !== "Sin asignar"
      ? (nameToIdRef.current.get(assignee) || null)
      : null;
    tasksStore
      .createTask({
        document_id: targetFile?.documentId ?? null,
        title,
        assignee_id: assigneeId,
        timeframe: timeframe === "month" ? "month" : "week",
      })
      .then(() => {
        toast.success("Tarea creada", `“${title}” fue agregada.`);
      })
      .catch(() => {
        toast.error("No se pudo crear la tarea", "Intenta de nuevo.");
      });
  }, [filesStore.files, tasksStore]);

  const toggleTask = useCallback((taskId: string) => {
    const task = tasksStore.tasks.find((t) => t.id === taskId);
    if (!task) return;
    tasksStore
      .updateTask(taskId, { completed: !task.completed, status: !task.completed ? "done" : "todo" })
      .catch(() => {
        toast.error("No se pudo actualizar la tarea");
      });
  }, [tasksStore]);

  const deleteTask = useCallback((taskId: string) => {
    tasksStore
      .deleteTask(taskId)
      .then(() => {
        toast.success("Tarea eliminada");
      })
      .catch(() => {
        toast.error("No se pudo eliminar la tarea");
      });
  }, [tasksStore]);

  const handleSetCurrentViewMode = useCallback((mode: ViewModeType) => {
    panesStore.setCurrentViewMode(mode);
    if (pathname && pathname !== "/workspace") {
      router.push("/workspace");
    }
  }, [panesStore, pathname, router]);

  const handleSetPaneViewType = useCallback((viewType: PaneViewType, pane: "left" | "right") => {
    panesStore.setPaneViewType(viewType, pane);
    if (pathname && pathname !== "/workspace") {
      router.push("/workspace");
    }
  }, [panesStore, pathname, router]);

  return (
    <WorkspaceContext.Provider
      value={{
        files: filesStore.files,
        activePane: panesStore.activePane,
        isSplit: panesStore.isSplit,
        leftPane: panesStore.leftPane,
        rightPane: panesStore.rightPane,
        currentViewMode: panesStore.currentViewMode as any,
        selectedProject,
        selectedMember,
        selectedTimeframe,
        tasks: tasksStore.tasks,
        workspaceHistory: historyStore.entries,
        projects: filesStore.spaces.map((s) => s.name),
        members,
        setCurrentViewMode: handleSetCurrentViewMode,
        reorderTabs: panesStore.reorderTabs,
        selectedFileIds,
        toggleSelectFile,
        clearSelection,
        setSelectedProject,
        setSelectedMember,
        setSelectedTimeframe,
        setActivePane: panesStore.setActivePane,
        setIsSplit: panesStore.setIsSplit,
        openFile,
        closeFile: panesStore.closeFile,
        setActiveTab: panesStore.setActiveTab,
        setEditorMode: panesStore.setEditorMode as any,
        setPaneViewType: handleSetPaneViewType,
        updateFileContent: filesStore.updateFileContent,
        updateFileTitle: filesStore.updateFileTitle,
        createFile,
        deleteFile: filesStore.deleteFile,
        moveFile: filesStore.moveFile,
        addTask,
        toggleTask,
        deleteTask,
        graphData,
        getBacklinks,
        addHistoryEntry: historyStore.addEntry,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error("useWorkspace must be used within a WorkspaceProvider");
  }
  return context;
}

export type { WorkspaceFile, PaneState, PaneViewType, ViewModeType, Task, HistoryEntry };
export { useFilesStore, usePanesStore, useTasksStore, useHistoryStore, useGraphData };